import { Link } from 'react-router-dom';
import { safeImageSrcSet, safeImageUrl, type PostSummary } from '../../lib/sanity';
import { CATEGORY_LABELS } from '../../pages/blog/categoryLabels';
import { renderedImageDimensions } from '../../lib/imageDimensions';

type BlogPostCardProps = {
  post: PostSummary;
};

export function BlogPostCard({ post }: BlogPostCardProps) {
  const imgUrl = safeImageUrl(post.coverImage, (b) =>
    b.width(800).height(450).auto('format'),
  );
  const imgSrcSet = safeImageSrcSet(
    post.coverImage,
    800,
    (b, width) => b.width(width).height(Math.round((width * 9) / 16)).auto('format'),
  );
  const imageDimensions = renderedImageDimensions(post.coverImage, 800);

  const date = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <Link
      to={`/blog/${post.slug.current}`}
      className="group flex flex-col overflow-hidden border border-white/10 bg-surface-low/60 transition-[transform,background] duration-300 hover:-translate-y-1 hover:bg-surface-container-high"
    >
      {imgUrl && (
        <div className="aspect-[16/9] overflow-hidden bg-surface-container">
          <img
            src={imgUrl}
            srcSet={imgSrcSet ?? undefined}
            alt={post.coverImage?.alt ?? post.title}
            width={imageDimensions?.width}
            height={imageDimensions?.height}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3 flex items-center gap-2">
          {post.category && (
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-primary">
              {CATEGORY_LABELS[post.category] ?? post.category}
            </span>
          )}
          {date && <span className="text-[13px] text-on-surface-muted">{date}</span>}
        </div>
        <h2 className="font-display m-0 text-lg leading-snug text-on-surface">{post.title}</h2>
        {post.excerpt && (
          <p className="mt-2 flex-1 text-[15px] leading-relaxed text-on-surface-variant">
            {post.excerpt}
          </p>
        )}
        {post.author?.name && (
          <p className="mt-4 m-0 text-[13px] text-on-surface-muted">By {post.author.name}</p>
        )}
      </div>
    </Link>
  );
}
